/**
 * ஸ்ரார்த்த திதி நிர்ணய இயந்திரம் — Shraddha Thithi Engine (ES Module)
 * 
 * Pure computation module — no I/O, no dependencies.
 * Port of the standalone shraddha-thithi-engine/index.js for browser use.
 *
 * Usage:
 *   import { computeShraddhaThithi } from './shraddhaEngine';
 *   const enrichedRows = computeShraddhaThithi(panchangamRows);
 *   // Each row now has: _engineShr, _aparahnaShare, _reason
 */

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const NUM_TO_NAME = {
  1:'பிரதமை',2:'துவிதியை',3:'த்ருதியை',4:'சதுர்த்தி',5:'பஞ்சமி',
  6:'ஷஷ்டி',7:'ஸப்தமி',8:'அஷ்டமி',9:'நவமி',10:'தசமி',
  11:'ஏகாதசி',12:'துவாதசி',13:'த்ரையோதசி',14:'சதுர்தசி',15:'பௌர்ணமி',
  16:'பிரதமை',17:'துவிதியை',18:'த்ருதியை',19:'சதுர்த்தி',20:'பஞ்சமி',
  21:'ஷஷ்டி',22:'ஸப்தமி',23:'அஷ்டமி',24:'நவமி',25:'தசமி',
  26:'ஏகாதசி',27:'துவாதசி',28:'த்ரையோதசி',29:'சதுர்தசி',30:'அமாவாஸை'
};

const UNAMES = {
  1:'சு-ப்ரதமை',2:'சு-துவிதியை',3:'சு-த்ருதியை',4:'சு-சதுர்த்தி',
  5:'சு-பஞ்சமி',6:'சு-ஷஷ்டி',7:'சு-ஸப்தமி',8:'சு-அஷ்டமி',
  9:'சு-நவமி',10:'சு-தசமி',11:'சு-ஏகாதசி',12:'சு-துவாதசி',
  13:'சு-த்ரையோதசி',14:'சு-சதுர்தசி',15:'பௌர்ணமி',
  16:'க்ரு-ப்ரதமை',17:'க்ரு-துவிதியை',18:'க்ரு-த்ருதியை',
  19:'க்ரு-சதுர்த்தி',20:'க்ரு-பஞ்சமி',21:'க்ரு-ஷஷ்டி',
  22:'க்ரு-ஸப்தமி',23:'க்ரு-அஷ்டமி',24:'க்ரு-நவமி',
  25:'க்ரு-தசமி',26:'க்ரு-ஏகாதசி',27:'க்ரு-துவாதசி',
  28:'க்ரு-த்ரையோதசி',29:'க்ரு-சதுர்தசி',30:'அமாவாஸை'
};

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

/** Convert nazhigai.vinaadi to minutes */
function nv(v) {
  if (!v && v !== 0) return 0;
  const p = String(v).split('.');
  let na = parseInt(p[0]) || 0, vi = parseInt(p[1]) || 0;
  if (p[1] && p[1].length === 1) vi *= 10;
  return na * 24 + vi * 0.4;
}

/** Short thithi name for display */
function shortTh(name) {
  if (!name) return '';
  return name.substring(0, Math.min(name.length, 4));
}

/** Build aparahna distribution string */
function buildAv(r) {
  if (r._corrTh2Name && r._s2 > 0 && r._s1 > 0)
    return `அபு-${r._apTotal} (${shortTh(r._corrTh1Name)}-${r._s1}, ${shortTh(r._corrTh2Name)}-${r._s2})`;
  else if (r._corrTh2Name && r._s2 > 0)
    return `அபு-${r._apTotal} (${shortTh(r._corrTh2Name)}-${r._s2})`;
  else if (r._s1 > 0)
    return `அபு-${r._apTotal} (${shortTh(r._corrTh1Name)}-${r._s1})`;
  else
    return `அபு-${r._apTotal} (${shortTh(r._corrTh1Name)}-L-${r._apTotal})`;
}

// ═══════════════════════════════════════════════════════════════════
// MAIN ENGINE
// ═══════════════════════════════════════════════════════════════════

/**
 * Compute Shraddha Thithi for an array of panchangam rows.
 * 
 * Each row must have: TamilMonthNumber, TamilDate, ThithiNumber,
 * ThithiNameInTamil, ThithiNazhigai, Thithi2Number, Thithi2NameInTamil,
 * AgasNazhigai (or AgasDuration)
 * 
 * Returns a new array of rows (shallow-copied) enriched with:
 *   _engineShr    — computed shraddha thithi name
 *   _aparahnaShare — aparahna distribution string
 *   _reason       — reasoning for the assignment
 * 
 * @param {Array} rawRows - panchangam data rows
 * @returns {Array} enriched rows
 */
export function computeShraddhaThithi(rawRows) {
  if (!rawRows || !rawRows.length) return [];

  // Shallow-copy to avoid mutating original
  const data = rawRows.map(r => ({ ...r }));

  // Normalize field types
  for (const r of data) {
    r.TamilMonthNumber = parseInt(r.TamilMonthNumber) || 0;
    r.TamilDate = parseInt(r.TamilDate) || 0;
  }

  // ─── Step 1: Th1 correction (சதுர்த்தி ↔ சதுர்தசி) ───
  let prevNum = 0;
  for (let i = 0; i < data.length; i++) {
    const r = data[i];
    const num = parseInt(r.ThithiNumber) || 0;
    const name = r.ThithiNameInTamil || '';
    let cn = num, cname = name;

    const nn = i + 1 < data.length ? (parseInt(data[i + 1].ThithiNumber) || 0) : 0;
    const nname = i + 1 < data.length ? (data[i + 1].ThithiNameInTamil || '') : '';

    if (num === 4 && name.includes('சதுர்த்தி') && (prevNum >= 10 || nn === 15 || nname.includes('பௌர்ண'))) {
      cn = 14; cname = 'சதுர்தசி';
    } else if (num === 19 && name.includes('சதுர்த்தி') && (prevNum >= 25 || nn === 30 || nname.includes('அமாவா'))) {
      cn = 29; cname = 'சதுர்தசி';
    } else if ((num === 14 || num === 29) && name.includes('சதுர்த்தி')) {
      cname = 'சதுர்தசி';
    }

    if (prevNum > 0 && !cn) {
      if (num === 4 && prevNum >= 10 && prevNum <= 13) { cn = 14; cname = 'சதுர்தசி'; }
      else if (num === 19 && prevNum >= 25 && prevNum <= 28) { cn = 29; cname = 'சதுர்தசி'; }
    }

    r._u1 = cn; r._corrTh1Name = cname;
    prevNum = cn;
  }

  // ─── Step 2: Th2 correction ───
  for (let i = 0; i < data.length; i++) {
    const r = data[i];
    const t2n = r.Thithi2NameInTamil || '';
    const t2num = parseInt(r.Thithi2Number) || 0;
    if (!t2n) { r._u2 = 0; r._corrTh2Name = ''; continue; }

    let cn = t2num, cname = t2n;
    if (t2num === 4 && t2n.includes('சதுர்த்தி') && r._u1 >= 12) { cn = 14; cname = 'சதுர்தசி'; }
    else if (t2num === 19 && t2n.includes('சதுர்த்தி') && r._u1 >= 27) { cn = 29; cname = 'சதுர்தசி'; }
    if ((t2num === 14 || t2num === 29) && t2n.includes('சதுர்த்தி')) cname = 'சதுர்தசி';

    if (i + 1 < data.length) {
      const nu = data[i + 1]._u1;
      const nn = data[i + 1]._corrTh1Name;
      if (cn !== nu) {
        const df = Math.abs(nu - r._u1);
        const skip = df >= 2 || (r._u1 >= 28 && nu <= 2);
        if (!skip) { cn = nu; cname = nn; }
      }
    }
    r._u2 = cn; r._corrTh2Name = cname;
  }

  // ─── Step 3: Aparahna vyapthi computation ───
  for (const r of data) {
    const ag = nv(r.AgasNazhigai) || 720;
    const th1e = nv(r.ThithiNazhigai);
    const ap = ag * 3 / 5, ae = ag * 4 / 5;
    r._apTotal = Math.round(ae - ap);
    r._s1 = Math.round(Math.max(0, Math.min(th1e, ae) - ap));
    r._s2 = r._corrTh2Name ? Math.round(Math.max(0, Math.min(ag, ae) - Math.max(th1e, ap))) : 0;
  }

  // ─── Step 4: Decision engine (per month) ───
  function processMonth(mn) {
    const ms = data.findIndex(r => r.TamilMonthNumber === mn);
    if (ms < 0) return;
    let me = ms;
    while (me + 1 < data.length && data[me + 1].TamilMonthNumber === mn) me++;

    const mDays = [];
    for (let i = ms; i <= me; i++) {
      const r = data[i];
      mDays.push({
        i, r, td: r.TamilDate,
        u1: r._u1, u2: r._u2 || 0,
        th1: r._corrTh1Name, th2: r._corrTh2Name || '',
        s1: r._s1, s2: r._s2,
        assigned: null, assignedU: 0, status: 'normal', reason: ''
      });
    }

    const maxTd = mDays.length;
    const isBoundary = (td) => (td <= 3 || td >= maxTd - 1);
    const isExpendable = (td) => (td === maxTd);

    // A. Assign max vyapthi thithi
    for (const d of mDays) {
      if (d.th2 && d.s2 > d.s1) {
        d.assigned = d.th2; d.assignedU = d.u2;
        d.reason = `${d.s2} நிமிடம்`;
      } else {
        d.assigned = d.th1; d.assignedU = d.u1;
        d.reason = d.s1 > 0 ? `${d.s1} நிமிடம்` : 'full';
      }
    }

    // B. Resolve doubles
    for (let iter = 0; iter < 30; iter++) {
      let any = false;
      const uMap = {};
      for (let j = 0; j < mDays.length; j++) {
        const u = mDays[j].assignedU;
        if (u <= 0) continue;
        if (!uMap[u]) uMap[u] = [];
        uMap[u].push(j);
      }

      for (const [u, indices] of Object.entries(uMap)) {
        if (indices.length <= 1) continue;
        let bestJ = indices[0], bestV = 0;
        const expIdx = indices.filter(j => isExpendable(mDays[j].td));
        const nonExpIdx = indices.filter(j => !isExpendable(mDays[j].td));

        if (expIdx.length > 0 && nonExpIdx.length > 0) {
          for (const j of nonExpIdx) {
            const d = mDays[j];
            const v = d.u1 === parseInt(u) ? d.s1 : d.s2;
            if (v >= bestV) { bestV = v; bestJ = j; }
          }
        } else {
          const allFull = indices.every(j => {
            const d = mDays[j];
            const v = d.u1 === parseInt(u) ? d.s1 : d.s2;
            return v >= data[d.i]._apTotal;
          });
          if (allFull) {
            bestJ = indices[indices.length - 1];
            bestV = (() => { const d = mDays[bestJ]; return d.u1 === parseInt(u) ? d.s1 : d.s2; })();
          } else {
            for (const j of indices) {
              const d = mDays[j];
              const v = d.u1 === parseInt(u) ? d.s1 : d.s2;
              if (v >= bestV) { bestV = v; bestJ = j; }
            }
          }
        }

        for (const j of indices) {
          if (j === bestJ) continue;
          const d = mDays[j];
          if (isBoundary(d.td)) {
            d.assigned = 'சூன்ய திதி'; d.assignedU = -1; d.status = 'soonya';
            d.reason = isExpendable(d.td)
              ? `சூன்ய: ${UNAMES[parseInt(u)] || u} மாத எல்லை (Td:${mDays[bestJ].td} வெற்றி)`
              : `சூன்ய: ${UNAMES[parseInt(u)] || u} Td:${mDays[bestJ].td} அதிக வ்யாப்தி (${bestV} நி)`;
            any = true;
          } else {
            const altU = d.u1 === parseInt(u) ? d.u2 : d.u1;
            const altName = d.u1 === parseInt(u) ? d.th2 : d.th1;
            const altS = d.u1 === parseInt(u) ? d.s2 : d.s1;
            if (altU > 0 && altName) {
              d.assignedU = altU; d.assigned = altName;
              d.reason = `${altName}: ${altS} நி (alt, ${UNAMES[parseInt(u)] || u} → Td:${mDays[bestJ].td})`;
              any = true;
            } else {
              d.assigned = 'அதிதி'; d.assignedU = -2; d.status = 'athithi';
              d.reason = 'அதிதி: திதி இல்லை';
            }
          }
        }
      }
      if (!any) break;
    }

    // C. Cross-month boundary check
    if (ms > 0) {
      const pr = data[ms - 1];
      const prevAssU = (pr._s2 > pr._s1 && pr._u2) ? pr._u2 : pr._u1;
      const fd = mDays[0];
      if (fd.assignedU > 0 && fd.status !== 'soonya' && fd.assignedU === prevAssU) {
        const otherHasU = mDays.some((d, j) => j > 0 && (d.u1 === fd.assignedU || d.u2 === fd.assignedU));
        if (!otherHasU) {
          const origU = fd.assignedU;
          fd.assigned = 'சூன்ய திதி'; fd.assignedU = -1; fd.status = 'soonya';
          fd.reason = `சூன்ய: ${UNAMES[origU] || origU} முன் மாதம் assigned (cross-month)`;
        }
      }
    }

    // D. Fill remaining
    for (const d of mDays) {
      if (d.assignedU > 0 || d.status === 'soonya' || d.status === 'athithi') continue;
      if (isBoundary(d.td)) {
        d.assigned = 'சூன்ய திதி'; d.assignedU = -1; d.status = 'soonya';
        d.reason = 'சூன்ய: திதி இல்லை (மாத எல்லை)';
      } else {
        d.assigned = 'அதிதி'; d.assignedU = -2; d.status = 'athithi';
        d.reason = 'அதிதி: திதி இல்லை';
      }
    }

    // Store results
    for (const d of mDays) {
      d.r._engineShr = d.assigned || '';
      d.r._reason = d.reason;
      d.r._aparahnaShare = buildAv(d.r);
    }
  }

  // Process all 12 months
  for (let mn = 1; mn <= 12; mn++) processMonth(mn);

  return data;
}

export default computeShraddhaThithi;
